// src/services/waitlistService.js
import { tenantDataService } from './tenantDataService';

const COLLECTION_NAME = 'waitlist';

/**
 * Get all waitlist entries
 * @returns {Promise<Array>}
 */
export async function getWaitlist() {
  try {
    const waitlist = await tenantDataService.getAll(COLLECTION_NAME);
    return waitlist || [];
  } catch (error) {
    console.error('Error loading waitlist:', error);
    return [];
  }
}

/**
 * Get waitlist entry by ID
 * @param {string} id - Waitlist entry ID
 * @returns {Promise<Object|null>}
 */
export async function getWaitlistEntry(id) {
  try {
    return await tenantDataService.getById(COLLECTION_NAME, id);
  } catch (error) {
    console.error('Error loading waitlist entry:', error);
    return null;
  }
}

/**
 * Add client to waitlist
 * @param {Object} waitlistData - Waitlist entry data
 * @returns {Promise<Object>}
 */
export async function addToWaitlist(waitlistData) {
  try {
    const entry = {
      ...waitlistData,
      id: waitlistData.id || Date.now().toString(),
      status: 'waiting',
      priority: waitlistData.priority || 'normal',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      notificationsSent: 0,
      lastContactedAt: null
    };
    
    await tenantDataService.create(COLLECTION_NAME, entry);
    return entry;
  } catch (error) {
    console.error('Error adding to waitlist:', error);
    throw error;
  }
}

/**
 * Update waitlist entry
 * @param {string} id - Entry ID
 * @param {Object} updates - Updates to apply
 * @returns {Promise<Object>}
 */
export async function updateWaitlistEntry(id, updates) {
  try {
    const updatedEntry = {
      ...updates,
      id,
      updatedAt: new Date().toISOString()
    };
    
    await tenantDataService.update(COLLECTION_NAME, id, updatedEntry);
    return updatedEntry;
  } catch (error) {
    console.error('Error updating waitlist entry:', error);
    throw error;
  }
}

/**
 * Remove client from waitlist
 * @param {string} id - Waitlist entry ID
 * @returns {Promise<boolean>}
 */
export async function removeFromWaitlist(id) {
  try {
    await tenantDataService.delete(COLLECTION_NAME, id);
    return true;
  } catch (error) {
    console.error('Error removing from waitlist:', error);
    return false;
  }
}

/**
 * Find suitable waitlist entries for a cancelled appointment
 * @param {Object} appointmentSlot - Available appointment slot
 * @returns {Promise<Array>}
 */
export async function findSuitableWaitlistEntries(appointmentSlot) {
  try {
    const waitlist = await getWaitlist();
    const { date, time, type, staffId, duration } = appointmentSlot;
    
    // Filter waitlist entries that match criteria
    const suitable = waitlist.filter(entry => {
      if (entry.status !== 'waiting') return false;
      
      // Check preferred appointment type
      if (entry.preferredType && entry.preferredType !== type) return false;
      
      // Check preferred staff
      if (entry.preferredStaff && entry.preferredStaff !== staffId) return false;
      
      // Check time preferences
      if (entry.timePreferences) {
        const appointmentHour = parseInt(time.split(':')[0]);
        const { morning, afternoon, evening } = entry.timePreferences;
        
        if (appointmentHour < 12 && !morning) return false;
        if (appointmentHour >= 12 && appointmentHour < 17 && !afternoon) return false;
        if (appointmentHour >= 17 && !evening) return false;
      }
      
      // Check date preferences (if any)
      if (entry.earliestDate && new Date(date) < new Date(entry.earliestDate)) return false;
      if (entry.latestDate && new Date(date) > new Date(entry.latestDate)) return false;
      
      return true;
    });
    
    // Sort by priority and creation date
    return suitable.sort((a, b) => {
      // Priority order: urgent > high > normal > low
      const priorityOrder = { urgent: 4, high: 3, normal: 2, low: 1 };
      const aPriority = priorityOrder[a.priority] || 2;
      const bPriority = priorityOrder[b.priority] || 2;
      
      if (aPriority !== bPriority) {
        return bPriority - aPriority; // Higher priority first
      }
      
      // Same priority, sort by creation date (first come, first served)
      return new Date(a.createdAt) - new Date(b.createdAt);
    });
  } catch (error) {
    console.error('Error finding suitable waitlist entries:', error);
    return [];
  }
}

/**
 * Mark waitlist entry as contacted
 * @param {string} id - Waitlist entry ID
 * @param {string} method - Contact method (email, phone, sms)
 * @returns {Promise<boolean>}
 */
export async function markAsContacted(id, method = 'email') {
  try {
    const entry = await getWaitlistEntry(id);
    if (!entry) return false;
    
    const updates = {
      lastContactedAt: new Date().toISOString(),
      lastContactMethod: method,
      notificationsSent: (entry.notificationsSent || 0) + 1
    };
    
    await updateWaitlistEntry(id, updates);
    return true;
  } catch (error) {
    console.error('Error marking as contacted:', error);
    return false;
  }
}

/**
 * Convert waitlist entry to appointment
 * @param {string} waitlistId - Waitlist entry ID
 * @param {Object} appointmentData - Appointment data
 * @returns {Promise<boolean>}
 */
export async function convertToAppointment(waitlistId, appointmentData) {
  try {
    // Update waitlist entry status
    await updateWaitlistEntry(waitlistId, {
      status: 'converted',
      convertedAt: new Date().toISOString(),
      appointmentId: appointmentData.id
    });
    
    return true;
  } catch (error) {
    console.error('Error converting to appointment:', error);
    return false;
  }
}

/**
 * Save waitlist (legacy compatibility)
 * @param {Array} waitlistEntries - Array of waitlist entries
 * @returns {Promise<void>}
 * @deprecated Use individual CRUD operations instead
 */
export async function saveWaitlist(waitlistEntries) {
  try {
    const promises = waitlistEntries.map(entry => {
      if (entry.id) {
        return updateWaitlistEntry(entry.id, entry);
      } else {
        return addToWaitlist(entry);
      }
    });
    
    await Promise.all(promises);
  } catch (error) {
    console.error('Error saving waitlist:', error);
    throw error;
  }
}

/**
 * Get waitlist statistics
 * @returns {Promise<Object>}
 */
export async function getWaitlistStats() {
  try {
    const waitlist = await getWaitlist();
    
    const stats = {
      total: waitlist.length,
      waiting: waitlist.filter(e => !e.status || e.status === 'waiting').length,
      converted: waitlist.filter(e => e.status === 'converted').length,
      cancelled: waitlist.filter(e => e.status === 'cancelled').length,
      byPriority: {
        urgent: waitlist.filter(e => e.priority === 'urgent').length,
        high: waitlist.filter(e => e.priority === 'high').length,
        normal: waitlist.filter(e => e.priority === 'normal').length,
        low: waitlist.filter(e => e.priority === 'low').length
      },
      averageWaitDays: 0 // Could calculate based on creation dates
    };
    
    return stats;
  } catch (error) {
    console.error('Error getting waitlist stats:', error);
    return { total: 0, waiting: 0, converted: 0, cancelled: 0, byPriority: {} };
  }
}
